import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EditorInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import AddIcon from '@atlaskit/icon/core/add';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import UploadIcon from '@atlaskit/icon/core/upload';
import { token } from '@atlaskit/tokens';

import {
	useRouteActions,
	createSingleQueryParamHook,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	setInEditorTemplatesPanelQueryParam,
	removeInEditorTemplatesQueryParam,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	CONTEXT_PANEL_TAB_TARGET,
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
	isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen,
} from '@confluence/template-utils/entry-points/urlUtils.ts';

import { i18n } from './bpqai18n.ts';
import { ButtonText } from './bpqaStyled.tsx';
import { BOEQuickActionButton } from './BOEQuickActionButton.tsx';
import type { PresetAPIType } from './bpqaConst.ts';

interface BlankObjectExperienceButtonsProps {
	disabled: boolean;
	editorPresetAPI?: PresetAPIType;
	isTitleFocused: boolean;
	focusEditor: () => Promise<void>;
	isAIEnabled: boolean;
	isLivePage?: boolean;
	isPublishedPage?: boolean;
	onCreateWithRovoClick: () => void;
	onCreateWithRovoHover?: () => void;
}

// Icon components hoisted outside to avoid recreating on every render
const CreateWithRovoIcon = ({ disabled }: { disabled: boolean }) => (
	<AiGenerativeTextIcon
		label="Create with Rovo"
		color={disabled ? token('color.icon.disabled') : token('color.icon.subtle')}
	/>
);

const AllTemplatesIcon = ({ disabled }: { disabled: boolean }) => (
	<TemplateIcon
		label="All templates"
		color={disabled ? token('color.icon.disabled') : token('color.icon.subtle')}
	/>
);

const MoreElementsIcon = ({ disabled }: { disabled: boolean }) => (
	<AddIcon
		size="medium"
		label=""
		color={disabled ? token('color.icon.disabled') : token('color.icon.subtle')}
	/>
);

const ImportIcon = ({ disabled }: { disabled: boolean }) => (
	<UploadIcon
		label="Import"
		color={disabled ? token('color.icon.disabled') : token('color.icon.subtle')}
	/>
);

// Create hooks for our query params
const useInEditorTemplatesPanelParam = createSingleQueryParamHook(
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
);
const useContextPanelTabTargetParam = createSingleQueryParamHook(CONTEXT_PANEL_TAB_TARGET);

export const BlankObjectExperienceButtons = ({
	disabled,
	editorPresetAPI,
	isTitleFocused,
	focusEditor,
	isAIEnabled,
	isLivePage,
	isPublishedPage,
	onCreateWithRovoClick,
	onCreateWithRovoHover,
}: BlankObjectExperienceButtonsProps): React.JSX.Element | null => {
	const [isOnImportTab, setIsOnImportTab] = useState(false);
	const { setQueryParams } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasTrackedInitialRenderRef = useRef(false);

	// Fire analytics on first render only (initial page load)
	useEffect(() => {
		if (!hasTrackedInitialRenderRef.current) {
			hasTrackedInitialRenderRef.current = true;
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'blankObjectExperienceButtons',
					attributes: {
						contentType: isLivePage ? 'liveDoc' : 'page',
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, isLivePage]);

	// Get query param values for the templates panel
	const inEditorTemplatesPanelValue = useInEditorTemplatesPanelParam();
	const contextPanelTabTargetValue = useContextPanelTabTargetParam();

	// Helper to get all query params (for compatibility with isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen)
	const getQueryParams = useCallback(
		() => ({
			[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: inEditorTemplatesPanelValue,
			[CONTEXT_PANEL_TAB_TARGET]: contextPanelTabTargetValue,
		}),
		[inEditorTemplatesPanelValue, contextPanelTabTargetValue],
	);

	// Add elements slash menu active state
	const isTypeAheadOpen =
		useSharedPluginStateSelector(
			editorPresetAPI as EditorInjectionAPI<any, any> | null | undefined,
			'typeAhead.isOpen',
		) ?? false;
	// Templates panel active state
	const isPanelOpen = isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen(getQueryParams);

	// Templates button is active when panel is open but NOT on import tab
	const isAllTemplatesLinkActive = isPanelOpen && !isOnImportTab;
	// Import button is active when panel is open AND on import tab
	const isImportActive = isPanelOpen && isOnImportTab;

	// Handle create with rovo click
	const handleCreateWithRovoClick = useCallback(() => {
		onCreateWithRovoClick();
	}, [onCreateWithRovoClick]);

	// Handle all templates click
	const handleAllTemplatesClick = useCallback(async () => {
		if (isAllTemplatesLinkActive) {
			// Close panel if templates panel is already open
			removeInEditorTemplatesQueryParam(setQueryParams, isAIEnabled);
		} else {
			// Open templates tab (panel will reactively update to templates mode)
			// Clear the contextPanelTabTarget so it defaults to templates
			setQueryParams({ [CONTEXT_PANEL_TAB_TARGET]: null }, true);
			setInEditorTemplatesPanelQueryParam(
				setQueryParams,
				IN_EDITOR_TEMPLATES_QUERY_OPEN,
				undefined,
				isAIEnabled,
			);
		}
		setIsOnImportTab(false);
	}, [setQueryParams, isAllTemplatesLinkActive, isAIEnabled]);

	// Handle import click
	const handleImportClick = useCallback(() => {
		if (isImportActive) {
			// Close panel if already on import tab
			removeInEditorTemplatesQueryParam(setQueryParams, isAIEnabled);
		} else {
			// Open import tab (panel will reactively update)
			setInEditorTemplatesPanelQueryParam(
				setQueryParams,
				IN_EDITOR_TEMPLATES_QUERY_OPEN,
				'import',
				isAIEnabled,
			);
		}
		setIsOnImportTab(!isImportActive);
	}, [setQueryParams, isImportActive, isAIEnabled]);

	// Handle more elements click
	const handleMoreElementsClick = useCallback(async () => {
		if (isTitleFocused) {
			await focusEditor();
		}

		if (isTypeAheadOpen) {
			editorPresetAPI?.typeAhead!.actions.close({ insertCurrentQueryAsRawText: true });
		} else {
			editorPresetAPI?.quickInsert!.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		}
	}, [editorPresetAPI, isTitleFocused, focusEditor, isTypeAheadOpen]);

	// Hide the entire BPQA when the slash command menu (typeahead) is open
	if (isTypeAheadOpen) {
		return null;
	}

	return (
		<Flex gap="space.100">
			{isAIEnabled && (
				<BOEQuickActionButton
					testId="create-with-rovo-button"
					onClick={handleCreateWithRovoClick}
					onMouseEnter={onCreateWithRovoHover}
					isDisabled={disabled}
					analyticsButtonType="cwr"
					isLivePage={isLivePage}
					icon={() => <CreateWithRovoIcon disabled={disabled} />}
					shortcut="Space"
				>
					<ButtonText isDisabled={disabled}>
						<FormattedMessage {...i18n.createWithRovo} />
					</ButtonText>
				</BOEQuickActionButton>
			)}

			{!isPublishedPage && (
				<BOEQuickActionButton
					testId="blank-page-quick-actions-all-templates-link"
					onClick={handleAllTemplatesClick}
					isDisabled={disabled}
					analyticsButtonType="template"
					isLivePage={isLivePage}
					icon={() => <AllTemplatesIcon disabled={disabled} />}
				>
					<ButtonText isDisabled={disabled}>
						<FormattedMessage {...i18n.chooseATemplate} />
					</ButtonText>
				</BOEQuickActionButton>
			)}

			<BOEQuickActionButton
				testId="blank-page-quick-actions-more-elements-link"
				onClick={handleMoreElementsClick}
				isDisabled={disabled}
				isActive={!!isTypeAheadOpen}
				analyticsButtonType="pageElements"
				isLivePage={isLivePage}
				icon={() => <MoreElementsIcon disabled={disabled} />}
				shortcut="/"
			>
				<ButtonText isDisabled={disabled}>
					<FormattedMessage {...i18n.addElements} />
				</ButtonText>
			</BOEQuickActionButton>

			{!isAIEnabled && (
				<BOEQuickActionButton
					testId="import-button"
					isDisabled={disabled}
					isActive={isImportActive}
					analyticsButtonType="import"
					isLivePage={isLivePage}
					icon={() => <ImportIcon disabled={disabled} />}
					onClick={handleImportClick}
				>
					<ButtonText isDisabled={disabled}>
						<FormattedMessage {...i18n.import} />
					</ButtonText>
				</BOEQuickActionButton>
			)}
		</Flex>
	);
};
